import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { consultationService } from '../services/consultationApi';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 2000;

type Method = 'aurora' | 'tarot';

const BemEstarPage: React.FC = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState<Method>('aurora');
  const [relato, setRelato] = useState('');
  const [pergunta, setPergunta] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!relato.trim() || isLoading) return;

    if (method === 'tarot') {
      const q = encodeURIComponent(relato.trim() + (pergunta.trim() ? ` — ${pergunta.trim()}` : ''));
      navigate(`/consultation?spread=tres-cartas-tempo&context=${q}`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const q = `${relato.trim()}${pergunta.trim() ? `\n\nPergunta: ${pergunta.trim()}` : ''}`;
      const result = await consultationService.askAurora(q, 'bem-estar');
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Bem-Estar Emocional</h1>
        <div className="w-20" />
      </div>
      <AuroraLoadingOverlay isLoading={isLoading} message="Aurora está meditando..." />

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">🌸</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Bem-Estar Emocional</h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Compartilhe o que está sentindo. Aurora oferece acolhimento, perspectivas e orientações práticas para o seu cuidado emocional.
          </p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por consulta</p>
        </div>

        <AnimatePresence mode="wait">
          {!answer && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>

              {/* Seleção de método */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {([
                  { id: 'aurora', icon: '🔮', label: 'Aurora Lyz', desc: 'Análise empática e orientação' },
                  { id: 'tarot',  icon: '🃏', label: 'Tarot',      desc: 'Tiragem de 3 cartas' },
                ] as { id: Method; icon: string; label: string; desc: string }[]).map(m => (
                  <button
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl text-center transition-all"
                    style={method === m.id
                      ? { background: 'rgba(244,63,94,0.15)', border: '1px solid rgba(244,63,94,0.45)' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <span className="text-2xl">{m.icon}</span>
                    <span className="text-sm font-semibold text-white">{m.label}</span>
                    <span className="text-xs text-white/45">{m.desc}</span>
                  </button>
                ))}
              </div>

              <div className="mb-1">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Como você está se sentindo</label>
              </div>
              <div className="relative mb-4">
                <textarea
                  value={relato}
                  onChange={e => setRelato(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Descreva seu estado emocional atual. Ex: Estou me sentindo sobrecarregada, com dificuldade de dormir e um vazio que não consigo nomear..."
                  rows={5}
                  className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                  style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                  disabled={isLoading}
                />
                <span className="absolute bottom-3 right-4 text-white/30 text-xs">{relato.length}/{MAX_CHARS}</span>
              </div>

              <div className="mb-1">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Pergunta <span className="normal-case text-white/35">(opcional)</span></label>
              </div>
              <input
                type="text"
                value={pergunta}
                onChange={e => setPergunta(e.target.value.slice(0, 300))}
                placeholder="Ex: Como posso recuperar minha paz interior?"
                className="w-full border rounded-2xl px-5 py-3 focus:outline-none text-sm mb-4"
                style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                disabled={isLoading}
              />

              <NoTokensBanner error={error} />

              <motion.button
                onClick={handleSubmit}
                disabled={!relato.trim() || isLoading}
                whileHover={relato.trim() && !isLoading ? { scale: 1.03 } : {}}
                whileTap={relato.trim() && !isLoading ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #be185d, #f43f5e)' }}
              >
                {isLoading ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aurora está acolhendo…</>
                ) : (
                  <><Sparkles size={18} />{method === 'tarot' ? 'Fazer Tiragem de Tarot' : 'Consultar Aurora Lyz'}</>
                )}
              </motion.button>
            </motion.div>
          )}

          {answer && (
            <motion.div key="answer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-white/45 text-xs mb-1">Seu relato</p>
                <p className="text-white/75 text-sm italic line-clamp-3">"{relato}"</p>
              </div>
              <div className="rounded-2xl p-4 sm:p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(190,24,93,0.12) 0%, rgba(244,63,94,0.08) 100%)', border: '1px solid rgba(244,63,94,0.25)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🌸</span>
                  <span className="text-pink-300 font-mystical font-semibold text-sm">Aurora Lyz — Bem-Estar Emocional</span>
                </div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>
              <button onClick={() => { setAnswer(''); setRelato(''); setPergunta(''); setError(null); }} className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium">
                Nova consulta
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BemEstarPage;

